import { BoxV2, MainTabsV2, PlanType } from '@labrav/react-components'
import { scrollspyTabs, useGetAllUsers, useUserType } from '@labrav/common'
import { useTheme } from '@mui/material'
import { useStyles } from './AddEditChannelOfferForm.styles'
import { ChannelOfferDetail } from '../ChannelOfferDetail/ChannelOfferDetail'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import type { ChannelPartnerPrivateOfferInCreation } from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { FormikProps, useFormikContext } from 'formik'
import { OfferMarkup } from '../OfferMarkup/OfferMarkup'
import { PrivateOfferStakeholders } from '../../../PrivateOfferFormV2/AwsOffer/PrivateOfferStakeholders/PrivateOfferStakeholders'
import { PrivateOfferCustomMetaData } from '../../../PrivateOfferFormV2/AwsOffer/PrivateOfferCustomeMetaData/PrivateOfferCustomMetaData'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../../../PrivateOfferFormV2/AzureOffer/MicrosoftFormFAQs/MicrosoftFormFAQs'
import { getIsErrorForChannelOfferTab } from '../../../../utils/channelOfferUtil'
import { FlexiblePaymentTerm } from '../FlexiblePaymentTerm/FlexiblePaymentTerm'
import { InstallmentsInfo } from '../InstallmentsInfo/InstallmentsInfo'
import {
  ContractDuration,
  getMappedMonthKey,
} from '../ContractDuration/ContractDuration'
import { ContractDurationType, DiscountType } from '../../../../types/flyout'
import { getPricesArray } from '../../../../modules/resaleAuthorization/utils/getPricesArray'
import { isNonEmptyNumber } from '@labrav/common'
import { ChannelOfferEula } from '../ChannelOfferEula/ChannelOfferEula'
import { ResellerAgreementAcceptance } from '../ResellerAgreementAcceptance/ResellerAgreementAcceptance'
import { ProductDimensions } from '../ProductDimensions/ProductDimensions'
import { AdditionalUsageAndSubscriptionDimensions } from '../AdditionalUsageAndSubscriptionDimensions/AdditionalUsageAndSubscriptionDimensions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { get } from 'lodash'
import { useSelector } from '../../../../store'
import type { FlyOutListingData } from '../../../../types/flyout'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { getShowContractDurationOption } from '../helpers'

export const AddEditChannelOfferForm = ({
  resellerAuthorization,
}: {
  resellerAuthorization?: ReceivedResellerAuthorization
}) => {
  const partnerType = useUserType()
  const { users } = useGetAllUsers(partnerType)
  const theme = useTheme()
  const offset = 240
  const { classes } = useStyles({ offset })
  const { flyoutSellerInfo } = useSelector<{
    flyoutSellerInfo: FlyOutListingData
  }>(state => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
  }))

  const isIndiaRegistered = flyoutSellerInfo?.countryRegistered === 'IN'
  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { values } = formikProps

  const priceValue = getPricesArray(resellerAuthorization?.dimensions || [])

  const showDurationSelection = getShowContractDurationOption({
    channelOffer: values,
    authorizationDuration: resellerAuthorization?.contractDurationType,
    isAuthorizationFlexible:
      resellerAuthorization?.discountType ===
      DiscountType.FLEXIBLE_PAYMENT_TERMS,
    resellerAuthorization,
  })

  const singleContractDurationValue =
    resellerAuthorization?.contractDurationType ===
      ContractDurationType.CUSTOM ||
    resellerAuthorization?.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS
      ? resellerAuthorization?.contractDurationInMonthsForEndCustomer
      : getMappedMonthKey(priceValue[0])

  const shouldShowSingleContractDurationMsg =
    isNonEmptyNumber(singleContractDurationValue) &&
    !showDurationSelection &&
    !(!values?.flexiblePaymentTerms && values?.variableStartDate)

  const singleContractDurationMsg = shouldShowSingleContractDurationMsg
    ? `Authorized contract duration by ${
        resellerAuthorization?.isvName
      }. : ${singleContractDurationValue} month${
        singleContractDurationValue! > 1 ? 's' : ''
      }`
    : ''

  const nonAdditionalDimensions = (values?.dimensions || []).filter(
    item =>
      !(item?.isAdditionalUsageAllowed || item?.type === 'additional_usage')
  )
  const code = resellerAuthorization?.currency || ''
  const additionalUsageDimensions = (values?.dimensions || []).filter(
    item => item?.isAdditionalUsageAllowed || item?.type === 'additional_usage'
  )
  const isAdditionalUsageDimensions = !!additionalUsageDimensions.length

  const isSassSubscription =
    resellerAuthorization?.productType === PlanType.SAAS_SUBSCRIPTION
  const channelOfferTabs = [
    {
      key: 'channel-offer-details',
      label: 'Offer details',
      renderElement: (
        <div
          id={'channel-offer-details'}
          data-testid={'channel-offer-details-section'}
          className={classes.scrollTopMargin}
        >
          <ChannelOfferDetail
            productType={
              resellerAuthorization?.productType || PlanType.SAAS_CONTRACT
            }
            buyerAwsAccountId={resellerAuthorization?.buyerAwsAccountId}
            noteDescription={
              isIndiaRegistered
                ? "As this account is based in India, you can only target buyers based in India. Buyers outside India won't be able to accept offers that you create."
                : ''
            }
          />
        </div>
      ),
      isError: getIsErrorForChannelOfferTab(
        'channel-offer-details',
        formikProps,
        resellerAuthorization?.productType || PlanType.SAAS_CONTRACT
      ),
    },
    {
      key: 'channel-offer-pricing',
      label: 'Pricing',
      renderElement: (
        <div
          id={'channel-offer-pricing'}
          data-testid={'channel-offer-pricing-section'}
          className={classes.scrollTopMargin}
        >
          {resellerAuthorization?.discountType !==
            DiscountType.FLEXIBLE_PAYMENT_TERMS &&
            !isSassSubscription && (
              <FlexiblePaymentTerm
                resaleAuthorization={resellerAuthorization}
              />
            )}
          {!isSassSubscription && (
            <ContractDuration
              specificDuration={singleContractDurationMsg}
              showDurationSelection={showDurationSelection}
              resellerAuthorization={resellerAuthorization}
            />
          )}
          <OfferMarkup
            productType={
              resellerAuthorization?.productType || PlanType.SAAS_CONTRACT
            }
            resaleAuthorization={resellerAuthorization}
            discountType={resellerAuthorization?.discountType}
          />
          {!isSassSubscription && (
            <ProductDimensions
              productType={
                resellerAuthorization?.productType || PlanType.SAAS_CONTRACT
              }
              resellerAuthorization={resellerAuthorization}
            />
          )}
          {isSassSubscription && (
            <BoxV2
              heading={'Usage dimensions'}
              headingIcon={
                <FontAwesomeIcon
                  icon={faShelves}
                  style={{ width: '18px', height: '14px' }}
                />
              }
            >
              <div className={classes.rowWrapper}>
                <div className={classes.tableSubHeading}>
                  The unit price for each usage dimension is determined by
                  applying the same markup ratio.
                </div>
                <AdditionalUsageAndSubscriptionDimensions
                  dimensions={nonAdditionalDimensions?.map(item => {
                    const resaleDimension = (
                      resellerAuthorization?.dimensions || []
                    )?.find(
                      resaleDimension => resaleDimension?.name === item?.name
                    )
                    const wholesalePrice = get(
                      resaleDimension?.prices,
                      'subscriptionPrice',
                      0
                    )
                    const buyerPrice = get(item?.prices, 'subscriptionPrice', 0)
                    return {
                      ...item,
                      wholesalePrice,
                      margin: buyerPrice ? buyerPrice - wholesalePrice : '',
                    }
                  })}
                  priceArray={[
                    {
                      keyName: 'wholesalePrice',
                      label: 'Wholesale unit price',
                    },
                    {
                      keyName: 'prices.subscriptionPrice',
                      label: 'Buyer unit price',
                    },
                    {
                      keyName: 'margin',
                      label: 'Margin',
                    },
                  ]}
                  currencySymbol={getCurrencySymbol(code)}
                />
              </div>
            </BoxV2>
          )}
          {isAdditionalUsageDimensions && (
            <BoxV2
              heading={'Usage dimensions'}
              headingIcon={
                <FontAwesomeIcon
                  icon={faShelves}
                  style={{ width: '18px', height: '14px' }}
                />
              }
            >
              <div className={classes.rowWrapper}>
                <div className={classes.tableSubHeading}>
                  The unit price for each usage dimension is determined by
                  applying the same markup ratio.
                </div>
                <AdditionalUsageAndSubscriptionDimensions
                  dimensions={additionalUsageDimensions?.map(item => {
                    const resaleDimension = (
                      resellerAuthorization?.dimensions || []
                    )?.find(
                      resaleDimension =>
                        resaleDimension?.name === item?.name &&
                        resaleDimension?.isAdditionalUsageAllowed
                    )
                    const wholesalePrice = get(
                      resaleDimension?.prices,
                      'additionalUsagePrice',
                      0
                    )
                    const buyerPrice = get(
                      item?.prices,
                      'additionalUsagePrice',
                      0
                    )

                    return {
                      ...item,
                      wholesalePrice,
                      margin: buyerPrice ? buyerPrice - wholesalePrice : '',
                    }
                  })}
                  priceArray={[
                    {
                      keyName: 'wholesalePrice',
                      label: 'Wholesale unit price',
                    },
                    {
                      keyName: 'prices.additionalUsagePrice',
                      label: 'Buyer unit price',
                    },
                    {
                      keyName: 'margin',
                      label: 'Margin',
                    },
                  ]}
                />
              </div>
            </BoxV2>
          )}
          {values?.flexiblePaymentTerms && (
            <InstallmentsInfo
              discountType={resellerAuthorization?.discountType}
              resaleAuthorization={resellerAuthorization}
            />
          )}
        </div>
      ),
      isError: getIsErrorForChannelOfferTab(
        'channel-offer-pricing',
        formikProps,
        resellerAuthorization?.productType
      ),
    },
    {
      key: 'channel-offer-other',
      label: 'Other',
      renderElement: (
        <div
          id={'channel-offer-other'}
          data-testid={'channel-offer-other-section'}
          className={classes.scrollTopMargin}
        >
          <ChannelOfferEula
            receivedResaleAuthEulaUrl={
              resellerAuthorization?.endCustomerEulaFiles
                ? resellerAuthorization?.endCustomerEulaFiles[0]
                : ''
            }
          />
          <PrivateOfferStakeholders
            users={users}
            stakeholdersFaqs={
              <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.STAKEHOLDERS} />
            }
          />
          <PrivateOfferCustomMetaData />
          {resellerAuthorization?.resellerLicenseAgreementFiles && (
            <ResellerAgreementAcceptance
              resellerLicenseAgreementUrl={
                resellerAuthorization?.resellerLicenseAgreementFiles[0]
              }
            />
          )}
        </div>
      ),
      isError: getIsErrorForChannelOfferTab(
        'channel-offer-other',
        formikProps,
        resellerAuthorization?.productType
      ),
    },
  ]
  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: channelOfferTabs,
    offset,
  })

  return (
    <div className={classes.root}>
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={channelOfferTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={'64px'}
      />
      {channelOfferTabs.map(
        channelOfferTabItem => channelOfferTabItem.renderElement
      )}
    </div>
  )
}

export { getShowContractDurationOption } from '../helpers'
